'use client';

import {
  useEffect,
  useState
} from 'react';

import {
  practiceVideoUrl
} from '@/app/lib/practice/pack';

// A topic's explainer clip (1080x1350, 4:5). The clips are uploaded
// separately from the code, so a topic may name a video that is not there
// yet. Each id is checked once per browser session with a HEAD request and
// the block is simply left out when the file is missing - no broken player,
// and no repeated 404s on every page.

const SESSION_KEY = 'es-practice-video-availability:v1';

const inFlight = new Map<string, Promise<boolean>>();

function readCache(): Record<string, boolean> {

  try {

    return JSON.parse(window.sessionStorage.getItem(SESSION_KEY) ?? '{}') as Record<string, boolean>;
  } catch {

    return {};
  }
}

function writeCache(url: string, ok: boolean): void {

  try {

    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({
      ...readCache(),
      [url]: ok
    }));
  } catch {

    // Storage blocked: we just check again next time.
  }
}

function videoExists(url: string): Promise<boolean> {

  const cached = readCache()[url];

  if (cached !== undefined) {

    return Promise.resolve(cached);
  }

  let pending = inFlight.get(url);

  if (!pending) {

    pending = fetch(url, {
      method: 'HEAD'
    })
      .then((res) => res.ok && (res.headers.get('content-type') ?? 'video/').startsWith('video/'))
      .catch(() => false)
      .then((ok) => {

        writeCache(url, ok);
        return ok;
      });
    inFlight.set(url, pending);
  }

  return pending;
}

export function PracticeVideo({
  videoId, title
}: { videoId: string
  title: string }) {

  const url = practiceVideoUrl(videoId);
  const [ available, setAvailable ] = useState(false);

  useEffect(() => {

    if (!url) {

      return;
    }

    let cancelled = false;

    void videoExists(url).then((ok) => {

      if (!cancelled) {

        setAvailable(ok);
      }
    });

    return () => {

      cancelled = true;
    };
  }, [ url ]);

  if (!url || !available) {

    return null;
  }

  return (
    <figure className="w-full max-w-xs">
      <video
        controls
        preload="none"
        playsInline
        className="w-full aspect-[4/5] rounded-2xl bg-black"
        aria-label={title}
        onError={() => setAvailable(false)}
      >
        <source src={url} type="video/mp4" />
      </video>
      <figcaption className="text-xs text-gray-400 mt-1.5">{title}</figcaption>
    </figure>
  );
}

/** "turning-points" -> "Turning points". */
export function videoTitle(videoId: string): string {

  const words = videoId.replace(/-/g, ' ');

  return words.charAt(0).toUpperCase() + words.slice(1);
}
